const POLL_INTERVAL_MS = 20;
const RMS_THRESHOLD = 0.015;
// 3 Samples à 20 ms, damit einzelne Knackser/Rauschen im Idle-Stream nicht als Sprache zählen
const REQUIRED_SAMPLES = 3;

/** Erkennt per Pegelmessung, wann auf dem Avatar-Audiostream tatsächlich hörbares Audio ankommt. */
export class AvatarAudioProbe {
    private ctx: AudioContext | null = null;
    private analyser: AnalyserNode | null = null;
    private source: MediaStreamAudioSourceNode | null = null;
    private data = new Uint8Array(1024);
    private intervalId: number | null = null;
    private streakStart: number | null = null;
    private streakCount = 0;
    private pendingStream: MediaStream | null = null;
    private readonly onAddTrack = () => {
        const stream = this.pendingStream;
        if (!stream || stream.getAudioTracks().length === 0) return;
        this.detachSource();
        this.connect(stream);
    };

    public get isAttached(): boolean {
        return this.analyser !== null;
    }

    public attach(stream: MediaStream): void {
        this.detachSource();
        if (stream.getAudioTracks().length === 0) {
            this.pendingStream = stream;
            stream.addEventListener('addtrack', this.onAddTrack);
            return;
        }
        this.connect(stream);
    }

    /** Startet die Messung; onAudible erhält den performance.now()-Zeitpunkt des Pegelbeginns. */
    public arm(onAudible: (at: number) => void): void {
        this.disarm();
        if (!this.analyser) return;
        void this.ctx?.resume().catch(() => { /* ohne User-Geste evtl. nicht erlaubt */ });
        this.intervalId = window.setInterval(() => {
            const rms = this.readRms();
            const now = performance.now();
            if (rms > RMS_THRESHOLD) {
                this.streakStart ??= now;
                this.streakCount++;
                if (this.streakCount >= REQUIRED_SAMPLES) {
                    const at = this.streakStart;
                    this.disarm();
                    onAudible(at);
                }
            } else {
                this.streakStart = null;
                this.streakCount = 0;
            }
        }, POLL_INTERVAL_MS);
    }

    public disarm(): void {
        if (this.intervalId !== null) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.streakStart = null;
        this.streakCount = 0;
    }

    public destroy(): void {
        this.disarm();
        this.detachSource();
        void this.ctx?.close().catch(() => { /* bereits geschlossen */ });
        this.ctx = null;
    }

    private connect(stream: MediaStream): void {
        try {
            this.ctx ??= new AudioContext();
            this.source = this.ctx.createMediaStreamSource(stream);
            this.analyser = this.ctx.createAnalyser();
            this.analyser.fftSize = this.data.length;
            // Nur an den Analyser, nicht an ctx.destination – sonst würde das Audio doppelt abgespielt
            this.source.connect(this.analyser);
        } catch (err) {
            console.warn('[AvatarAudioProbe] attach failed:', err);
            this.detachSource();
        }
    }

    private detachSource(): void {
        if (this.pendingStream) {
            this.pendingStream.removeEventListener('addtrack', this.onAddTrack);
            this.pendingStream = null;
        }
        this.source?.disconnect();
        this.source = null;
        this.analyser = null;
    }

    private readRms(): number {
        if (!this.analyser) return 0;
        this.analyser.getByteTimeDomainData(this.data);
        let sum = 0;
        for (const v of this.data) {
            const n = (v - 128) / 128;
            sum += n * n;
        }
        return Math.sqrt(sum / this.data.length);
    }
}
